import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiFacebook,
  FiTwitter,
  FiInstagram,
  FiYoutube,
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiArrowRight,
  FiSend,
} from "react-icons/fi";
import { MdSchool } from "react-icons/md";

const quickLinks = [
  { label: "About Us", path: "/about" },
  { label: "Academics", path: "/academics" },
  { label: "Admissions", path: "/admissions" },
  { label: "Student Life", path: "/student-life" },
  { label: "News & Events", path: "/news-events" },
  { label: "Gallery", path: "/gallery" },
  { label: "Notices", path: "/notices" },
  { label: "Results", path: "/results" },
  { label: "Contact", path: "/contact" },
];

const programLinks = [
  { label: "Primary School (1–5)", path: "/academics#primary" },
  { label: "Secondary School (6–10)", path: "/academics#secondary" },
  { label: "High School (11–12)", path: "/academics#highschool" },
  { label: "Apply Online", path: "/admissions/apply" },
  { label: "Scholarships", path: "/admissions#scholarships" },
];

export default function Footer() {
  const [email, setEmail] = useState("");

  return (
    <footer className="bg-primary-900 text-white">
      {/* gradient accent strip */}
      <div className="h-1 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500" />

      {/* main */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
          {/* school info */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center ring-1 ring-white/15">
                <MdSchool className="w-7 h-7 text-accent-400" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-base sm:text-xl leading-snug">
                  Paradise English School
                </h3>
                <p className="text-accent-400 text-xs tracking-widest uppercase">
                  Inspiring Excellence
                </p>
              </div>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              Paradise English School has been nurturing
              young minds with quality education since 2000. Committed to
              academic excellence, character building, and holistic development.
            </p>
            <div className="flex items-center gap-2.5">
              {[
                { icon: FiFacebook, href: "#", label: "Facebook" },
                { icon: FiTwitter, href: "#", label: "Twitter" },
                { icon: FiInstagram, href: "#", label: "Instagram" },
                { icon: FiYoutube, href: "#", label: "YouTube" },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 rounded-lg bg-white/8 hover:bg-accent-500 flex items-center justify-center transition-colors"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* quick links */}
          <div>
            <h4 className="font-bold text-accent-400 uppercase tracking-widest text-xs mb-5">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm group"
                  >
                    <FiArrowRight className="w-3 h-3 text-accent-400 group-hover:translate-x-1 transition-transform" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* programs + newsletter */}
          <div>
            <h4 className="font-bold text-accent-400 uppercase tracking-widest text-xs mb-5">
              Programs
            </h4>
            <ul className="space-y-2">
              {programLinks.map((link, i) => (
                <li key={i}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm group"
                  >
                    <FiArrowRight className="w-3 h-3 text-accent-400 group-hover:translate-x-1 transition-transform" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h4 className="font-bold text-accent-400 uppercase tracking-widest text-xs mb-3">
                Newsletter
              </h4>
              <p className="text-white/45 text-xs mb-3">
                Subscribe for updates and news
              </p>
              <div className="flex gap-2 min-w-0">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-white/8 border border-white/15 text-white placeholder-white/30 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                />
                <button className="p-2.5 bg-accent-500 hover:bg-accent-600 rounded-lg transition-colors shrink-0">
                  <FiSend className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* contact */}
          <div>
            <h4 className="font-bold text-accent-400 uppercase tracking-widest text-xs mb-5">
              Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <FiMapPin className="w-4 h-4 text-accent-400 mt-0.5 shrink-0" />
                <p className="text-white/50 text-sm">
                  Kanchan Rural Municipality, Ward No. 5
                  <br />
                  Rupandehi District, Lumbini Province, Nepal
                </p>
              </div>
              <div className="flex items-start gap-3">
                <FiPhone className="w-4 h-4 text-accent-400 mt-0.5 shrink-0" />
                <div>
                  <a
                    href="tel:+977-1-4123456"
                    className="text-white/50 hover:text-white transition-colors text-sm block"
                  >
                    +977-1-4123456
                  </a>
                  <a
                    href="tel:+977-9841234567"
                    className="text-white/50 hover:text-white transition-colors text-sm block"
                  >
                    +977-9841234567
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FiMail className="w-4 h-4 text-accent-400 shrink-0" />
                <a
                  href="mailto:paradise2058@gmail.com"
                  className="text-white/50 hover:text-white transition-colors text-sm"
                >
                  paradise2058@gmail.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <FiClock className="w-4 h-4 text-accent-400 mt-0.5 shrink-0" />
                <div className="text-white/50 text-sm">
                  <p>Sun – Fri: 7:00 AM – 5:00 PM</p>
                  <p>Saturday: 9:00 AM – 1:00 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* bottom bar */}
      <div className="border-t border-white/8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between items-center gap-3 text-center sm:text-left">
          <p className="text-white/35 text-xs sm:text-sm">
            &copy; {new Date().getFullYear()} Paradise English School. All
            rights reserved.
          </p>
          <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-white/35">
            <Link
              to="/about"
              className="hover:text-accent-400 transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="opacity-40">•</span>
            <Link
              to="/about"
              className="hover:text-accent-400 transition-colors"
            >
              Terms of Use
            </Link>
            <span className="opacity-40">•</span>
            <Link
              to="/contact"
              className="hover:text-accent-400 transition-colors"
            >
              Support
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
